import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Button,
    Alert,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    Paper,
    Stack,
    Link as MuiLink
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom"; 
import config from './config.js';
import api from './api';
import AdminTools from './AdminTools'; // Import the new component

// Helper component for Brand Visualizer (copied from BrandClarity.js)
const BrandVisualizer = ({ visualCues, onSave, userId }) => {
    const [colors, setColors] = useState(visualCues?.colors || []);
    const [fonts] = useState(visualCues?.fonts || []);
    const [savingVisuals, setSavingVisuals] = useState(false);
    const [visualsError, setVisualsError] = useState('');
    const [visualsSuccess, setVisualsSuccess] = useState('');

    useEffect(() => {
        // Dynamically load Google Fonts
        if (fonts.length > 0) {
            const fontFamilies = fonts.join('|').replace(/ /g, '+');
            const link = document.createElement('link');
            link.href = `https://fonts.googleapis.com/css?family=${fontFamilies}&display=swap`;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
            
            return () => {
                document.head.removeChild(link);
            };
        }
    }, [fonts]);

    const handleColorChange = (index, newColor) => {
        const updatedColors = [...colors];
        updatedColors[index] = newColor;
        setColors(updatedColors);
    };

    const handleSaveColors = async () => {
        setSavingVisuals(true);
        setVisualsError('');
        setVisualsSuccess('');
        try {
            const payload = {
                ai_visual_cues: {
                    ...visualCues,
                    colors: colors
                }
            };
            await api.put(`/api/brand/visuals`, payload);
            setVisualsSuccess('Color palette saved successfully!');
            if(onSave) {
                onSave(payload.ai_visual_cues);
            }
        } catch (err) {
            setVisualsError(err.response?.data?.message || 'Failed to save color palette.');
        } finally {
            setSavingVisuals(false);
        }
    };
    
    const generateGoogleFontUrl = (fontName) => {
        return `https://fonts.google.com/specimen/${fontName.replace(/ /g, '+')}`;
    };

    if (!visualCues || (!visualCues.colors?.length && !visualCues.fonts?.length)) {
        return null;
    }

    return (
        <Paper elevation={2} sx={{p: 2, mt: 3, backgroundColor: 'grey.50'}}>
            <Typography variant="h6" gutterBottom>Brand Visualizer</Typography>
            
            {visualsError && <Alert severity="error" sx={{mb:2}}>{visualsError}</Alert>}
            {visualsSuccess && <Alert severity="success" sx={{mb:2}}>{visualsSuccess}</Alert>}

            {colors.length > 0 && (
                <Box mb={2}>
                    <Typography variant="subtitle1" gutterBottom>Color Palette</Typography>
                    <Stack direction="row" spacing={2} sx={{alignItems: 'center', flexWrap: 'wrap'}}>
                        {colors.map((color, index) => (
                            <Box key={index} sx={{ textAlign: 'center' }}>
                                <Paper sx={{ width: 80, height: 80, backgroundColor: color, border: '1px solid #ddd' }} />
                                <input 
                                    type="color" 
                                    value={color}
                                    onChange={(e) => handleColorChange(index, e.target.value)}
                                    style={{ border: 'none', width: '80px', height: '25px', cursor: 'pointer', marginTop: '4px' }}
                                />
                                <Typography variant="caption" display="block">{color}</Typography>
                            </Box>
                        ))}
                    </Stack>
                    <Button onClick={handleSaveColors} disabled={savingVisuals} size="small" variant="contained" sx={{mt: 1}}>
                        {savingVisuals ? <CircularProgress size={20} /> : 'Save Colors'}
                    </Button>
                </Box>
            )}

            {fonts.length > 0 && (
                 <Box>
                    <Typography variant="subtitle1" gutterBottom>Font Suggestions</Typography>
                    {fonts.map((font, index) => (
                        <Card key={index} variant="outlined" sx={{mb: 1}}>
                            <CardContent>
                                <MuiLink href={generateGoogleFontUrl(font)} target="_blank" rel="noopener noreferrer" sx={{textDecoration:'none'}}>
                                    <Typography sx={{ fontFamily: font, fontSize: '1.5rem', color: 'primary.main' }}>
                                        {font}
                                    </Typography>
                                </MuiLink>
                                <Typography sx={{ fontFamily: font }}>
                                    The quick brown fox jumps over the lazy dog.
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}
        </Paper>
    );
};


// Helper function to determine ACS Style Name
const getAcsStyleName = (proactivity, reactivity) => {
    if (proactivity > 0 && reactivity > 0) return "Expressive";
    if (proactivity < 0 && reactivity < 0) return "Analytical";
    if (proactivity > 0 && reactivity < 0) return "Driver";
    if (proactivity < 0 && reactivity > 0) return "Amiable";
    if (proactivity === 0 && reactivity > 0) return "Amiable Tendency";
    if (proactivity === 0 && reactivity < 0) return "Analytical Tendency";
    if (reactivity === 0 && proactivity > 0) return "Driver Tendency";
    if (reactivity === 0 && proactivity < 0) return "Analytical Tendency"; 
    if (proactivity === 0 && reactivity === 0) return "Balanced/Neutral";
    return "Not Determined"; 
};

// Helper function to determine Charisma Zone
const getCharismaZoneName = (warmth, confidence) => {
    if (warmth === null || warmth === undefined || confidence === null || confidence === undefined) return "N/A";
    const wq = parseFloat(warmth); 
    const cq = parseFloat(confidence);
    if (wq > 0 && cq > 0) return "Charismatic Zone";
    if (wq > 0 && cq <= 0) return "Warmth Zone";
    if (wq <= 0 && cq > 0) return "Confidence Zone";
    if (wq <= 0 && cq <= 0) return "Danger Zone!";
    return "Not Determined";
};


function Dashboard(props) {
    const { 
        openSurvey, 
        hasCompletedSurvey, 
        userId, 
        userName, // Accept the new userName prop
        userRole,
        userTeamId, 
        spiIsVisible,     
        spiIsCompleted,   
        spiCohortId,
        prsIsVisible,
        prsIsConfigured,
        charismaIsVisible, 
        charismaIsCompleted, 
        charismaCohortId,
        brandIsVisible,
        brandIsCompleted
    } = props;

    const [loadingReports, setLoadingReports] = useState(true);
    const [reports, setReports] = useState([]);
    const [fetchReportsError, setFetchReportsError] = useState(null);
    
    // State for the 360 summary from BrandClarity summaries
    const [threeSixtySummary, setThreeSixtySummary] = useState(null);
    const [loadingThreeSixtySummary, setLoadingThreeSixtySummary] = useState(false);

    // State for BrandClarity data
    const [brandClarityData, setBrandClarityData] = useState(null);
    const [loadingBrandClarity, setLoadingBrandClarity] = useState(false);

    const [spiSummaryData, setSpiSummaryData] = useState(null);
    const [spiSummaryLoading, setSpiSummaryLoading] = useState(false);
    const [spiSummaryError, setSpiSummaryError] = useState(null);

    const [prsDashboardResults, setPrsDashboardResults] = useState(null);
    const [loadingPrsDashboardResults, setLoadingPrsDashboardResults] = useState(false);
    const [prsDashboardError, setPrsDashboardError] = useState(null);

    const [charismaSummaryData, setCharismaSummaryData] = useState(null);
    const [charismaSummaryLoading, setCharismaSummaryLoading] = useState(false);
    const [charismaSummaryError, setCharismaSummaryError] = useState(null);


    // Fetch 360 Reports
    useEffect(() => {
        setFetchReportsError(null);
        const fetchReports = async () => {
            if (!userId) {
                setReports([]); setLoadingReports(false); return;
            }
            setLoadingReports(true);
            try {
                const response = await api.get(`${config.backendUrl}/reports?user_id=${userId}`);
                setReports(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                setFetchReportsError(error.response?.data?.message || error.message || "Failed to load 360 reports.");
                setReports([]);
            } finally { setLoadingReports(false); }
        };
        if (userId && userTeamId) fetchReports(); else { setLoadingReports(false); setReports([]); }
    }, [userId, userTeamId]);

    // Fetch Assessment Summaries (for 360)
    useEffect(() => {
        const fetchAssessmentSummaries = async () => {
            if (userId && userTeamId) {
                setLoadingThreeSixtySummary(true);
                try {
                    const response = await api.get('/api/brand/summary');
                    if (response.data && response.data.three_sixty) {
                        setThreeSixtySummary(response.data.three_sixty);
                    }
                } catch (error) {
                    console.error("Failed to load 360 summary", error);
                } finally {
                    setLoadingThreeSixtySummary(false);
                }
            }
        };
        fetchAssessmentSummaries();
    }, [userId, userTeamId]);

    // Fetch BrandClarity Data
    const fetchBrandData = useCallback(async () => {
        if(userId && brandIsVisible) {
            setLoadingBrandClarity(true);
            try {
                const response = await api.get('/api/brand/responses');
                if (response.data) {
                    setBrandClarityData(response.data);
                }
            } catch (error) {
                if (error.response?.status !== 404) {
                    console.error('Failed to load brand clarity data.', error);
                }
                 setBrandClarityData(null); // Ensure no old data is shown on error
            } finally {
                setLoadingBrandClarity(false);
            }
        }
    }, [userId, brandIsVisible]);

    useEffect(() => {
        fetchBrandData();
    }, [fetchBrandData]);

    // Fetch SPI Summary
    useEffect(() => {
        const fetchSpiSummary = async () => {
            if (spiIsVisible && spiIsCompleted && userId && spiCohortId) {
                setSpiSummaryLoading(true); setSpiSummaryError(null);
                try {
                    const response = await api.get(`${config.backendUrl}/api/spi/results/${userId}/${spiCohortId}`);
                    setSpiSummaryData(response.data);
                } catch (error) {
                    setSpiSummaryError(error.response?.data?.message || error.message || "Failed to load SPI summary.");
                    setSpiSummaryData(null);
                } finally { setSpiSummaryLoading(false); }
            } else { 
                setSpiSummaryData(null); 
                setSpiSummaryLoading(false); 
            }
        };
        fetchSpiSummary();
    }, [spiIsVisible, spiIsCompleted, userId, spiCohortId]);

    // Fetch PRS Summary
    useEffect(() => {
        const fetchPrsDashboardSummary = async () => {
            if (prsIsVisible && prsIsConfigured && userId) {
                setLoadingPrsDashboardResults(true);
                setPrsDashboardError(null);
                try {
                    const response = await api.get(`${config.backendUrl}/api/prs/results`); 
                    setPrsDashboardResults(response.data);
                } catch (error) {
                    console.error("Error fetching PRS dashboard summary:", error);
                    setPrsDashboardError(error.response?.data?.message || "Failed to load PRS summary.");
                    setPrsDashboardResults(null);
                } finally {
                    setLoadingPrsDashboardResults(false);
                }
            } else {
                setPrsDashboardResults(null); 
                setLoadingPrsDashboardResults(false);
            }
        };
        fetchPrsDashboardSummary();
    }, [prsIsVisible, prsIsConfigured, userId]);

    // Fetch Charisma Summary
    useEffect(() => {
        const fetchCharismaSummary = async () => {
            if (charismaIsVisible && charismaIsCompleted && userId && charismaCohortId) {
                setCharismaSummaryLoading(true); setCharismaSummaryError(null);
                try {
                    const response = await api.get(`${config.backendUrl}/api/charisma/results/${userId}/${charismaCohortId}`);
                    setCharismaSummaryData(response.data);
                } catch (error) {
                    setCharismaSummaryError(error.response?.data?.message || error.message || "Failed to load Charisma Diagnostic summary.");
                    setCharismaSummaryData(null);
                } finally { setCharismaSummaryLoading(false); }
            } else {
                setCharismaSummaryData(null);
                setCharismaSummaryLoading(false); 
            }
        };
        fetchCharismaSummary();
    }, [charismaIsVisible, charismaIsCompleted, userId, charismaCohortId]);


    const completedReportRounds = useMemo(() => {
        if (!Array.isArray(reports) || reports.length === 0) return [];
        const userReport = reports[0]; 
        if (!userReport || !Array.isArray(userReport.rounds)) return [];
        return userReport.rounds
            .map(round => round.round_info) 
            .filter(roundInfo => roundInfo && roundInfo.survey_id != null && roundInfo.survey_name != null)
            .sort((a, b) => new Date(b.end_date || 0) - new Date(a.end_date || 0)); 
    }, [reports]);

    
    const extractKeywordsFromSummary = (summaryText, currentUserName) => {
        if (!summaryText) return [];

        // Define markers for the sections we're interested in.
        const identityMarker = /brand identity/i;
        const voiceMarker = /brand voice/i;
        const visualMarker = /visual design/i;

        let textToProcess = '';
        
        const identityIndex = summaryText.search(identityMarker);
        const voiceIndex = summaryText.search(voiceMarker);
        const visualIndex = summaryText.search(visualMarker);

        if (identityIndex !== -1) {
            const endOfIdentity = voiceIndex !== -1 ? voiceIndex : (visualIndex !== -1 ? visualIndex : summaryText.length);
            textToProcess += summaryText.substring(identityIndex, endOfIdentity);
        }

        if (voiceIndex !== -1) {
            const endOfVoice = visualIndex !== -1 ? visualIndex : summaryText.length;
            textToProcess += ' ' + summaryText.substring(voiceIndex, endOfVoice);
        }

        if (!textToProcess) {
             textToProcess = summaryText;
        }

        const keywords = new Set();
        const stopWords = new Set(['a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 'how', 'in', 'is', 'it', 'of', 'on', 'or', 'that', 'the', 'this', 'to', 'was', 'what', 'when', 'where', 'who', 'will', 'with', 'the', 'your', 'brand', 'identity', 'voice', 'you']);
        
        // **NEW LOGIC**: Add variations of the user's name to the stopwords
        if (currentUserName) {
            const nameParts = currentUserName.toLowerCase().split(' ');
            nameParts.forEach(part => {
                if(part.length > 1) { // Avoid adding single letters
                    stopWords.add(part);
                    stopWords.add(part + 's'); // e.g., "johns"
                }
            });
        }
        
        textToProcess.toLowerCase().split(/[\s,.;:!?]+/).forEach(word => {
            const cleanWord = word.replace(/[^a-z]/g, '');
            if (cleanWord.length > 3 && !stopWords.has(cleanWord)) {
                keywords.add(cleanWord);
            }
        });
        
        return Array.from(keywords).slice(0, 8);
    };
    
    // RENDER FUNCTIONS
    const renderSpiSummary = () => {
        if (spiSummaryLoading) return <CircularProgress size={24} sx={{display: 'block', mx: 'auto', my: 2}} />;
        if (spiSummaryError) return <Alert severity="warning" sx={{mt:1, textAlign: 'left'}}>{spiSummaryError}</Alert>;
        if (!spiSummaryData) return <Typography variant="body2" color="text.secondary" sx={{textAlign: 'left'}}>Summary not available.</Typography>;

        const { summary_calculations: calc, sections } = spiSummaryData;
        const acsStyleName = getAcsStyleName(
            (calc?.attitudinal_communication_style?.proactivity_score || 0) / 2,
            (calc?.attitudinal_communication_style?.reactivity_score || 0) / 2
        );
        const selectedAdjectives = sections?.Adjectives?.items?.[0]?.selected_adjectives || [];
        const informalRoleFullName = sections?.['Informal Team Roles']?.items?.[0]?.selected_role || "";
        const informalRoleNameForChip = informalRoleFullName.split(':')[0]; 
        const strongMotivators = calc?.personal_motivators_strong_interests || [];

        return (
            <Box sx={{textAlign: 'left'}}> 
                <Typography variant="body2" sx={{mb: 1, textAlign: 'left'}}>
                    Summary of your Self-Perception Inventory responses:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{fontWeight: 'bold', mr: 1, textAlign: 'left'}}>Attitudinal Communication Style:</Typography>
                    <Chip label={acsStyleName} size="small" color="primary" />
                </Box>
                <Typography variant="body2" sx={{mb: 0.5, textAlign: 'left'}}>
                    Your Composite Proactivity Score: {( (calc?.attitudinal_communication_style?.proactivity_score || 0) / 2).toFixed(1)}
                </Typography>
                <Typography variant="body2" sx={{mb: 1, textAlign: 'left'}}>
                    Your Composite Reactivity Score: {((calc?.attitudinal_communication_style?.reactivity_score || 0) / 2).toFixed(1)}
                </Typography>
                
                <Typography variant="subtitle2" sx={{mt: 1.5, mb:0.5, fontWeight: 'bold', textAlign: 'left'}}>Work Environment Preferences:</Typography>
                <Grid container spacing={0.5}>
                    {calc?.work_environment_preferences_dimensions && Object.entries(calc.work_environment_preferences_dimensions).map(([dim, val]) =>(
                        <Grid item xs={6} sm={4} md={3} key={dim}>
                             <Typography variant="caption" sx={{textAlign: 'left'}}>{dim}: {val.average_score?.toFixed(2) ?? 'N/A'}</Typography>
                        </Grid>
                    ))}
                </Grid>

                {selectedAdjectives.length > 0 &&
                    <Box sx={{mt:1.5, mb:0.5}}>
                        <Typography variant="body2" component="span" sx={{fontWeight: 'bold', mr: 1, textAlign: 'left'}}>Adjectives:</Typography>
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5}}>
                            {selectedAdjectives.map((adj, index) => (
                                <Chip key={index} label={adj} size="small" />
                            ))}
                        </Box>
                    </Box>
                }
                {informalRoleNameForChip &&
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, mt: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{fontWeight: 'bold', mr: 1, textAlign: 'left'}}>Preferred Team Role:</Typography>
                        <Chip label={informalRoleNameForChip} size="small" />
                    </Box>
                }
                {strongMotivators.length > 0 &&
                     <Box sx={{mt:1, mb:1}}>
                        <Typography variant="body2" component="span" sx={{fontWeight: 'bold', mr: 1, textAlign: 'left'}}>Strong Motivators:</Typography>
                         <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5}}>
                            {strongMotivators.map((interest, index) => (
                                <Chip key={index} label={interest} size="small" color="success" />
                            ))}
                        </Box>
                    </Box>
                }
            </Box>
        );
    };

    const renderPrsSummary = () => {
        if (loadingPrsDashboardResults) return <CircularProgress size={24} sx={{display: 'block', mx: 'auto', my: 2}} />;
        if (prsDashboardError) return <Alert severity="warning" sx={{mt:1, textAlign: 'left'}}>{prsDashboardError}</Alert>;
        
        if (prsDashboardResults && prsDashboardResults.number_of_respondents > 0) {
            const { behavioral_communication_style, adjectives, communication_methods } = prsDashboardResults;
            const sortedAdjectives = adjectives ? Object.entries(adjectives).sort(([,aCount],[,bCount]) => bCount - aCount) : [];
            const sortedCommMethods = communication_methods ? Object.entries(communication_methods).sort(([,aCount],[,bCount]) => bCount - aCount) : [];
            
            const spiUserAdjectives = spiSummaryData?.sections?.Adjectives?.items?.[0]?.selected_adjectives || [];
            const spiUserAdjectivesSet = new Set(spiUserAdjectives);

            return (
                <Box sx={{textAlign: 'left'}}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Summary of <strong>{prsDashboardResults.number_of_respondents}</strong> response(s):
                    </Typography>
                    <Box sx={{ my: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>Average Behavioral Communication Style:</Typography>
                            <Chip label={behavioral_communication_style?.style_name || 'N/A'} size="small" color="secondary" />
                        </Box>
                        <Typography variant="body2">Composite Proactivity: {behavioral_communication_style?.average_proactivity_score?.toFixed(1) ?? 'N/A'}</Typography>
                        <Typography variant="body2">Composite Reactivity: {behavioral_communication_style?.average_reactivity_score?.toFixed(1) ?? 'N/A'}</Typography>
                    </Box>
                    {sortedAdjectives.length > 0 && (
                        <Box sx={{ my: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, textAlign: 'left' }}>Adjectives Mentioned:</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {sortedAdjectives.map(([adj, count]) => { 
                                    const isShared = spiUserAdjectivesSet.has(adj);
                                    return (
                                        <Chip 
                                            key={adj} 
                                            label={`${adj} (${count})`} 
                                            size="small" 
                                            variant="outlined" 
                                            sx={isShared ? { backgroundColor: '#fffde7' } : {}} 
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                     {sortedCommMethods.length > 0 && (
                        <Box sx={{ my: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, textAlign: 'left' }}>Communication Methods Used:</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {sortedCommMethods.map(([method, count]) => <Chip key={method} label={`${method} (${count})`} size="small" variant="outlined" />)}
                            </Box>
                        </Box>
                    )}
                </Box>
            );
        } else if (prsIsConfigured) { 
             return (
                <Typography sx={{mb:1, textAlign: 'left'}}>
                    Your Professional Reputation Survey is active. Manage settings, view responses, or share your link.
                </Typography>
             );
        }
        return null; 
    };

    const renderCharismaSummary = () => {
        if (charismaSummaryLoading) return <CircularProgress size={24} sx={{display: 'block', mx: 'auto', my: 2}} />;
        if (charismaSummaryError) return <Alert severity="warning" sx={{mt:1, textAlign: 'left'}}>{charismaSummaryError}</Alert>;
        if (!charismaSummaryData) return <Typography variant="body2" color="text.secondary" sx={{textAlign: 'left'}}>Summary not available.</Typography>;

        const { warmth_quotient, confidence_quotient } = charismaSummaryData;
        const zone = getCharismaZoneName(warmth_quotient, confidence_quotient);
        
        return (
            <Box sx={{textAlign: 'left'}}>
                <Typography variant="body2" sx={{mb: 1, textAlign: 'left'}}>
                    Summary of your Charisma Diagnostic results:
                </Typography>
                <Typography variant="body2" sx={{mb: 0.5, textAlign: 'left'}}>
                    Warmth Quotient: <strong>{warmth_quotient !== null ? warmth_quotient.toFixed(2) : 'N/A'}</strong>
                </Typography>
                <Typography variant="body2" sx={{mb: 0.5, textAlign: 'left'}}>
                    Confidence Quotient: <strong>{confidence_quotient !== null ? confidence_quotient.toFixed(2) : 'N/A'}</strong>
                </Typography>
                 <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{fontWeight: 'bold', mr: 1, textAlign: 'left'}}>Identified Zone:</Typography>
                    <Chip label={zone} size="small" color={zone === "Charismatic Zone" ? "success" : (zone === "Danger Zone!" ? "error" : "info")} />
                </Box>
            </Box>
        );
    };
    
    const renderBrandClaritySummary = () => {
        if (loadingBrandClarity) return <CircularProgress size={24} />;
        if (!brandClarityData || !brandClarityData.ai_summary) return null;

        const summaryKeywords = extractKeywordsFromSummary(brandClarityData.ai_summary, userName);

        return (
            <Grid item xs={12} md={6} lg={4}>
                <Card elevation={2} sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                    <CardContent sx={{flexGrow: 1, textAlign: 'left'}}>
                        <Typography variant="h6" gutterBottom align="center">Brand Clarity AI Summary</Typography>
                        <Divider sx={{my:1}}/>
                        <Typography variant="body2" sx={{mb:1.5}}>A brief summary of your AI-generated brand recommendations:</Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{fontWeight: 'bold', mr: 1, textAlign: 'left'}}>Keywords:</Typography>
                             {summaryKeywords.map(kw => <Chip key={kw} label={kw} size="small" variant="outlined" sx={{mr:0.5, mb:0.5}}/>)}
                        </Box>

                        <BrandVisualizer 
                            visualCues={brandClarityData.ai_visual_cues}
                            onSave={(updatedCues) => setBrandClarityData(prev => ({...prev, ai_visual_cues: updatedCues}))}
                            userId={userId}
                        />

                    </CardContent>
                     <Box sx={{ p: 2, pt:0, mt: 'auto', display: 'flex', justifyContent: 'center' }}>
                         <Button component={RouterLink} to="/brand-clarity" variant="outlined" size="small">View Full Brand Strategy</Button>
                    </Box>
                </Card>
            </Grid>
        );
    };

    // Show message if user is not on a team
    if (!userTeamId) {
        return (
             <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <Typography variant="h4" gutterBottom component="h1" align="center">
                    Dashboard
                </Typography>
                 {(userRole === '1' || userRole === '2') && (
                    <Box sx={{ mb: 3 }}>
                        <AdminTools userId={userId} userRole={userRole} />
                    </Box>
                 )}
                <Paper elevation={3} sx={{p: 3, mt: 2}}>
                     <Typography variant="body1" align="center">
                        Please contact the Radian team to learn more about accessing Radian's suite of professional reputation tools.
                     </Typography>
                </Paper>
            </Box>
        );
    }
    
    const visibleCards = [
        prsIsVisible, 
        spiIsVisible, 
        charismaIsVisible, 
        userTeamId, // 360 survey card
        userTeamId && completedReportRounds.length > 0, // 360 reports card
        brandClarityData?.ai_summary // Brand Clarity card
    ].filter(Boolean).length;
    
    let gridSizeMd = 12 / (Math.ceil(visibleCards / 2) || 1);
    let gridSizeLg = 12 / (Math.ceil(visibleCards / 3) || 1);
    if(visibleCards === 1) { gridSizeMd = 12; gridSizeLg = 12; }
    if(visibleCards === 2) { gridSizeMd = 6; gridSizeLg = 6; }
    if(visibleCards === 3) { gridSizeMd = 4; gridSizeLg = 4; }
    if(visibleCards === 4) { gridSizeMd = 6; gridSizeLg = 3; }
    if(visibleCards === 5) { gridSizeMd = 4; gridSizeLg = 4; }
    if(visibleCards >= 6) { gridSizeMd = 4; gridSizeLg = 4; }


    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            <Typography variant="h4" gutterBottom component="h1" align="center">
                Dashboard
            </Typography>

            {(fetchReportsError) && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{fetchReportsError}</Alert>}
            
            {(userRole === '1' || userRole === '2') && (
                <Box sx={{ mb: 3 }}>
                    <AdminTools userId={userId} userRole={userRole} />
                </Box>
            )}

            {loadingReports ? ( 
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {/* PRS Card */}
                    {prsIsVisible && (
                        <Grid item xs={12} md={gridSizeMd} lg={gridSizeLg}>
                             <Card elevation={2} sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                <CardContent sx={{flexGrow: 1, textAlign: 'left'}}>
                                    <Typography variant="h6" gutterBottom align="center">Professional Reputation Survey</Typography>
                                    <Divider sx={{my:1}}/>
                                    {renderPrsSummary()} 
                                    {!prsIsConfigured && prsIsVisible && (
                                        <Typography sx={{mb:1, textAlign: 'left'}}>
                                            Set up your Professional Reputation Survey to gather external feedback.
                                        </Typography>
                                    )}
                                </CardContent>
                                <Box sx={{ p: 2, pt:0, mt: 'auto', display: 'flex', justifyContent: 'center' }}>
                                    <Button 
                                        component={RouterLink} 
                                        to="/assessment/prs/config" 
                                        variant="outlined" 
                                        size="small"      
                                        sx={{ width:'fit-content' }} 
                                    >
                                        {prsIsConfigured ? "Manage and View PRS" : "Configure PRS"}
                                    </Button>
                                </Box>
                            </Card>
                        </Grid>
                    )}

                    {/* SPI Card */}
                    {spiIsVisible && (
                        <Grid item xs={12} md={gridSizeMd} lg={gridSizeLg}>
                            <Card elevation={2} sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                <CardContent sx={{flexGrow: 1, textAlign: 'left'}}> 
                                    <Typography variant="h6" gutterBottom align="center">Self-Perception Inventory</Typography>
                                    <Divider sx={{my:1}}/>
                                    {spiIsCompleted ? (
                                        renderSpiSummary()
                                    ) : (
                                        <Box sx={{textAlign: 'left'}}>
                                            <Typography sx={{mb:1, textAlign: 'left'}}>
                                                Your Self-Perception Inventory is available.
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                                <Box sx={{ p: 2, pt:0, mt: 'auto', display: 'flex', justifyContent: 'center' }}>
                                    {spiIsCompleted ? (
                                         <Button component={RouterLink} to="/assessment/spi/results" variant="outlined" size="small" sx={{ width:'fit-content' }}>
                                            View Full SPI Report
                                        </Button>
                                    ) : (
                                        <Button 
                                            component={RouterLink} 
                                            to="/assessment/spi/survey" 
                                            variant="contained" 
                                            color="secondary"
                                            sx={{width:'fit-content'}}
                                        >
                                            Complete Your SPI
                                        </Button>
                                    )}
                                </Box>
                            </Card>
                        </Grid>
                    )}

                    {/* Charisma Diagnostic Card */}
                    {charismaIsVisible && (
                        <Grid item xs={12} md={gridSizeMd} lg={gridSizeLg}>
                            <Card elevation={2} sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                <CardContent sx={{flexGrow: 1, textAlign: 'left'}}>
                                    <Typography variant="h6" gutterBottom align="center">Charisma Diagnostic</Typography>
                                    <Divider sx={{my:1}}/>
                                    {charismaIsCompleted ? (
                                        renderCharismaSummary()
                                    ) : (
                                        <Box sx={{textAlign: 'left'}}>
                                            <Typography sx={{mb:1, textAlign: 'left'}}>
                                                Discover your unique blend of warmth and confidence.
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                                <Box sx={{ p: 2, pt:0, mt: 'auto', display: 'flex', justifyContent: 'center' }}>
                                    {charismaIsCompleted ? (
                                        <Button component={RouterLink} to="/assessment/charisma/results" variant="outlined" size="small" sx={{ width:'fit-content' }}>
                                            View Detailed Charisma Results 
                                        </Button>
                                    ) : (
                                        <Button 
                                            component={RouterLink} 
                                            to="/assessment/charisma/survey" 
                                            variant="contained" 
                                            color="info" 
                                            sx={{width:'fit-content'}}
                                        >
                                            Take Charisma Diagnostic
                                        </Button>
                                    )}
                                </Box>
                            </Card>
                        </Grid>
                    )}
                    
                    {/* Brand Clarity Card - Renders conditionally inside the function */}
                    {renderBrandClaritySummary()}

                    {/* 360 Evaluation Survey Card */}
                    {userTeamId && (
                         <Grid item xs={12} md={gridSizeMd} lg={gridSizeLg}>
                            <Card elevation={2} sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                <CardContent sx={{flexGrow: 1, textAlign: 'left'}}> 
                                    <Typography variant="h6" gutterBottom align="center">360° Evaluation Survey Rounds</Typography>
                                    <Divider sx={{my:1}}/>
                                    {openSurvey && openSurvey.id ? (
                                        hasCompletedSurvey ? (
                                            <Typography sx={{textAlign: 'left'}}>You have completed the current 360° survey: <strong>{openSurvey.name}</strong>.</Typography>
                                        ) : ( <Box sx={{textAlign: 'left'}}><Typography>The 360° survey round <strong>{openSurvey.name}</strong> is currently open for your cohort.</Typography></Box> ) 
                                    ) : (
                                        <Typography sx={{textAlign: 'left'}}>No open 360° survey rounds currently scheduled for your cohort.</Typography>
                                    )}
                                </CardContent>
                                 {openSurvey && openSurvey.id && !hasCompletedSurvey && (
                                    <Box sx={{ p: 2, pt:0, mt: 'auto', display: 'flex', justifyContent: 'center' }}>
                                        <Button component={RouterLink} to={`/survey-rounds/${openSurvey.id}/questions`} variant="contained" color="primary" >
                                            Start 360° Survey: {openSurvey.name}
                                        </Button>
                                    </Box>
                                )}
                            </Card>
                        </Grid>
                    )}
                   

                    {/* 360 Reports Card */}
                    {userTeamId && (
                        <Grid item xs={12} md={gridSizeMd} lg={gridSizeLg}>
                            <Card elevation={2} sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                <CardContent sx={{flexGrow: 1, textAlign: 'left'}}> 
                                    <Typography variant="h6" gutterBottom align="center">360° Evaluation Reports</Typography>
                                    <Divider sx={{my:1}}/>
                                    
                                    {/* 360 Summary Section */}
                                    {loadingThreeSixtySummary ? <CircularProgress size={20}/> : threeSixtySummary ? (
                                        <Box sx={{mb: 2}}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                                                <Typography variant="body2" sx={{fontWeight: 'bold', mr: 1}}>Top Keywords (All Rounds):</Typography>
                                                {threeSixtySummary.keywords?.map(kw => <Chip key={kw} label={kw} size="small" variant="outlined" sx={{mr:0.5, mb:0.5}} />)}
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                                                <Typography variant="body2" sx={{fontWeight: 'bold', mr: 1}}>Avg Contribution Score:</Typography>
                                                <Chip label={`${threeSixtySummary.contribution_score} (Team High Avg: ${threeSixtySummary.team_high_contribution})`} size="small" />
                                            </Box>
                                             <Divider sx={{my:2}}/>
                                        </Box>
                                    ) : null}

                                    {/* List of Reports */}
                                    {completedReportRounds.length > 0 ? (
                                        <List dense disablePadding>
                                            {completedReportRounds.slice(0, 5).map((roundInfo, index) => { 
                                                if (!roundInfo || roundInfo.survey_id == null) return null;
                                                let formattedDate = 'N/A';
                                                try {
                                                    if (roundInfo.end_date) {
                                                        const dateObj = new Date(roundInfo.end_date);
                                                        if (!isNaN(dateObj.getTime())) {
                                                            formattedDate = dateObj.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
                                                        }
                                                    }
                                                } catch (e) { console.error("Error formatting report date", e); }

                                                if (!userId) return <ListItem key={`invalid-user-${index}`}><ListItemText primary="Error: User ID missing for report link." sx={{textAlign: 'left'}}/></ListItem>;
                                                const reportKey = `${roundInfo.survey_id}-${userId}`;
                                                const reportLink = `/report/${roundInfo.survey_id}/${userId}`;

                                                return (
                                                    <React.Fragment key={reportKey}>
                                                        <ListItem button component={RouterLink} to={reportLink} sx={{ py: 0.5, pl:0, textAlign: 'left' }}>
                                                            <ListItemText
                                                                primary={roundInfo.survey_name || `Survey ${roundInfo.survey_id}`}
                                                                secondary={`Ended: ${formattedDate}`}
                                                                primaryTypographyProps={{ variant: 'body1', textAlign: 'left' }}
                                                                secondaryTypographyProps={{ variant: 'body2', textAlign: 'left' }}
                                                            />
                                                        </ListItem>
                                                        {index < completedReportRounds.slice(0, 5).length - 1 && <Divider component="li" />}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </List>
                                    ) : (
                                        <Typography sx={{textAlign: 'left'}}>No completed 360° reports available for you yet.</Typography>
                                    )}
                                </CardContent>
                                 {completedReportRounds.length > 0 && (
                                    <Box sx={{p:2, pt:0, mt: 'auto', display: 'flex', justifyContent: 'center'}}>
                                        <Button component={RouterLink} to="/reports-landing" size="small">View All Reports</Button>
                                    </Box>
                                )}
                            </Card>
                        </Grid>
                    )}
                </Grid>
            )}
        </Box>
    );
}

export default Dashboard;
